/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { AutocompleteItemDto, ID, WithVersion } from '@gh/common-shared-data';
import { createEnum, EnumSet } from '@gh/core-data';
import { CommunicationAttachmentDto } from '../../communications';
import { ContactDeliveryMethod } from '../../contacts/model/contact-details-shared.dto';
import { LetterDto } from '../../letters';

export const JobVariationStatus = createEnum('VARIATION_STATUS', {
  Quote: 1,
  Approved: 2,
  Reversed: 3,
  Rejected: 4,
});

export type JobVariationStatus = EnumSet<typeof JobVariationStatus>;

export type JobChangeOrderListMasterInfoDto = WithVersion<{
  id: ID;
  jobNumber: string;
  siteAddress: string;
  contactName: string;
  contactId: ID;
  isFinalized?: boolean;
  isNotProceeding?: boolean;
}>;
export type JobChangeOrderListItemDto = {
  id: ID;
  number: number;
  dateCreated: Date;
  description: string;
  timeExtensionDays: number;
  status: JobVariationStatus;
  drawStage: string;
  isDrawn: boolean;
};

export type JobChangeOrderListDto = {
  job: JobChangeOrderListMasterInfoDto;
  changeOrders: JobChangeOrderListItemDto[];
};

export type JobChangeOrderDto = {
  id: ID;
  number: number;
  dateCreated: Date;
  description: string;
  timeExtensionDays: number;
  status: JobVariationStatus;
  amount: number;
  amountWithGst: number;
};

export type JobChangeOrderTabListTotalDto = {
  status: JobVariationStatus;
  timeExtensionTotal: number;
  totalAmount: number;
  totalAmountWithGst: number;
};

export type JobChangeOrderTabListDto = {
  variations: JobChangeOrderDto[];
  totals: JobChangeOrderTabListTotalDto[];
};

export type JobChangeOrderDetailsDto = {
  changeOrder: JobChangeOrderDetailsMainDto;
  drawStages: JobChangeOrderDrawStageDto[];
};

export type JobChangeOrderDetailsSaveDto = {
  jobChangeOrder: JobChangeOrderDetailsMainDto;
  showQuantity: boolean;
  showUom: boolean;
  showPrice: boolean;
  isSave: boolean;
  printOnProcess: boolean;
};

export type JobChangeOrderDetailsPreviewDto = {
  jobChangeOrder: JobChangeOrderDetailsMainDto;
  showQuantity: boolean;
  showUom: boolean;
  showPrice: boolean;
};

export type JobChangeOrderDetailsSaveResponseDto = {
  jobChangeOrder: JobChangeOrderDetailsMainDto;
  affectedCostCentersCount: number;
  letterTemplate?: LetterDto;
};

export type JobChangeOrderConfirmationItemDto = {
  costCenterCode: number;
  itemCode: string;
};

export type JobChangeOrderDetailsMainDto = WithVersion<{
  variationId: ID;
  memo: string;
  status: JobVariationStatus;
  addedDate: Date;
  jobNumber: string;
  drawRuleId: ID;
  drawRuleName: string;
  drawRuleStage?: number;
  description: string;
  siteAddress: string;
  customerName: string;
  customerEmail: string;
  producedDate: Date;
  priceBookId?: ID;
  priceBookName?: string;
  variationNumber: number;
  timeExtensionDays: number;
  isNotProceeding: boolean;
  isFinalized: boolean;
  isSave: boolean;
  isRounded: boolean;
  isClaimed: boolean;
  isLendersSchedule: boolean;
  isGstApplies: boolean;
  variationItems: JobChangeOrderDetailsElementDto[];
  drawRuleStages: AutocompleteItemDto[];
  confirmation: JobChangeOrderConfirmationItemDto[];
  // derived info
  amount: number;
  gstAmount: number;
  jobAmountWithGst: number;
  jobVariationsAmountWithGst: number;
  amountWithGst: number;
  variationTotalPriceWithGST?: number;
  ordersCount: number;
  ordersCountPrint: number;
  taxRate?: number;
  taxType?: ID;
  taxName?: string;
  deliveryBy: ContactDeliveryMethod;
  invoiceDate?: Date;
}>;

export type JobChangeOrderDrawStageDto = {
  stageNumber: number;
  stageName: string;
  accountingDate?: Date;
};

export type JobChangeOrderDetailsElementDto = {
  variationItemId: ID;
  itemId: ID;
  memo: string;
  price: number;
  uomCode: string;
  quantity: number;
  deletePct: number;
  description: string;
  origQuantity: number;
  orderQuantity: number;
  costCenterCode: number;
  costCenterId: ID;
  priceExtension: number;
  priceWithGst?: number;
  code: string;
  isInPriceBook: boolean;
  isComponent: boolean;
  isCredit: boolean;
};

export type JobChangeOrderElementIdsGetResponseDto = {
  itemIds: ID[];
  componentIds: ID[];
};

export type JobChangeOrderGeneratedDocumentSetDto = {
  coQuotationDoc?: CommunicationAttachmentDto;
  coConfirmationDoc?: CommunicationAttachmentDto;
  coInvoiceDoc?: CommunicationAttachmentDto;
  progressDrawStatementDoc?: CommunicationAttachmentDto;
  jobOrderDocs?: Hash<CommunicationAttachmentDto>;
  replaceJobOrder?: boolean;
};

export type JobChangeOrderPrintRequestDto = {
  printerId?: ID;
  authCode?: string;
  docs: JobChangeOrderGeneratedDocumentSetDto;
};

export type JobChangeOrderProduceRequestDto = {
  printQuoteCopy: boolean;
  replaceJobOrder: boolean;
  showQuantity: boolean;
  showUom: boolean;
  showPrice: boolean;
  validAffectedCostCenters: boolean;
};

export type JobChangeOrderReplaceOrdersRequestDto = {
  validAffectedCostCenters: boolean;
};

export type JobChangeOrderProduceResponseDto = {
  affectedCostCentersCount: number;
};
